/**
 * Shared Helper Functions
 * Cross-cutting utilities used by multiple domains
 */

import { customAlphabet } from 'nanoid'
import { getTotalPotAmount } from '@ppwr/poker-engine'
import { normalizePots } from './adapters'
import type { QueryCtx, MutationCtx } from '../_generated/server'
import type { Id, Doc } from '../_generated/dataModel'

// Exclude confusing characters: 0/O, 1/I/L - uses only uppercase for consistency
const alphanumeric6 = customAlphabet('ABCDEFGHJKMNPQRSTUVWXYZ23456789', 6)

/**
 * Get all players for a game, sorted by seat
 * @param ctx - Convex context
 * @param gameId - Game ID
 * @returns Array of players sorted by seatId
 */
export async function getPlayersByGame(ctx: QueryCtx | MutationCtx, gameId: Id<'games'>) {
  const players = await ctx.db
    .query('players')
    .withIndex('by_game', (q) => q.eq('gameId', gameId))
    .collect()

  return players.sort((a, b) => a.seatId - b.seatId)
}

/**
 * Symmetrical seat assignment order for multiplayer games
 * Matches the visual layout used in practice games with bots
 *
 * Seat layout (10-seat table):
 * - Seat 0: Bottom center (host/first player)
 * - Seats 1-4: Right side (bottom to top)
 * - Seat 5: Top center
 * - Seats 6-9: Left side (top to bottom)
 *
 * Join order creates a balanced visual distribution:
 * Player 1 → Seat 0 (bottom center)
 * Player 2 → Seat 7 (left side)
 * Player 3 → Seat 3 (right side)
 * Player 4 → Seat 8 (left side)
 * Player 5 → Seat 2 (right side)
 * Player 6 → Seat 9 (left side)
 * Player 7 → Seat 1 (right side)
 * Player 8 → Seat 6 (left side, near top)
 * Player 9 → Seat 4 (right side, near top)
 * Player 10 → Seat 5 (top center)
 */
export const SYMMETRICAL_SEAT_ORDER = [0, 7, 3, 8, 2, 9, 1, 6, 4, 5]

/**
 * Find next available seat in a game using symmetrical seating pattern
 * @param ctx - Convex context
 * @param gameId - Game ID
 * @param maxPlayers - Maximum players allowed
 * @returns Next available seat number or null if full
 */
export async function findNextAvailableSeat(
  ctx: QueryCtx | MutationCtx,
  gameId: Id<'games'>,
  maxPlayers: number
): Promise<number | null> {
  const players = await getPlayersByGame(ctx, gameId)
  const takenSeats = new Set(players.map((p) => p.seatId))

  // Use symmetrical seating order for balanced visual distribution
  for (const seat of SYMMETRICAL_SEAT_ORDER) {
    if (seat < maxPlayers && !takenSeats.has(seat)) {
      return seat
    }
  }

  return null // Game is full
}

/**
 * Check if a player has acted in the current round
 * Derives from game.currentHandActions and player currentBets instead of maintaining separate boolean
 * This eliminates the hasActedThisRound anti-pattern!
 *
 * CRITICAL: This function must compare the player's CURRENT BET (from players table)
 * against the HIGHEST CURRENT BET at the table, NOT action amounts from history.
 * Action amounts are INCREMENTAL (how much was added), not TOTAL bets.
 *
 * @param player - Player document
 * @param game - Game document
 * @param allPlayers - All players in the game (needed to find highest current bet)
 * @returns True if player has acted this round and their bet matches the highest bet
 */
export function hasPlayerActedThisRound(
  player: Doc<'players'>,
  game: Doc<'games'>,
  allPlayers: Doc<'players'>[]
): boolean {
  // Folded/out/awaiting_rebuy players are considered as having acted (terminal states)
  if (player.state === 'folded' || player.state === 'out' || player.state === 'awaiting_rebuy') {
    return true
  }

  // All-in players are considered as having acted (can't act further)
  if (player.state === 'allin') {
    return true
  }

  // Get player's actions this round (excluding blind posts)
  const playerActions = game.currentHandActions.filter(
    (action) => action.playerId === player._id && action.boardState === game.boardState && action.action !== 'blind'
  )

  // If no actions this round, player hasn't acted
  if (playerActions.length === 0) {
    return false
  }

  // Get highest current bet from all players (CRITICAL FIX)
  // Use player.currentBet (total chips in front of player), NOT action amounts
  const highestCurrentBet = Math.max(0, ...allPlayers.map((p) => p.currentBet))

  // Player has acted appropriately if:
  // 1. They have at least one action this round, AND
  // 2. Their current bet >= highest current bet at the table
  //
  // Example:
  // - Player A bets 100 (currentBet = 100)
  // - Player B calls 100 (currentBet = 100) ✅ hasActed = true
  // - Player C raises to 300 (currentBet = 300)
  // - Player A now has currentBet = 100, highest = 300 - hasActed = false (needs to respond)
  // - Player B also has currentBet = 100, highest = 300 - hasActed = false (needs to respond)
  return player.currentBet >= highestCurrentBet
}

/**
 * Get the last action a player took this round
 * Provides richer information than a boolean flag (what action? when?)
 *
 *
 * @param player - Player document
 * @param game - Game document
 * @returns Last action or undefined if player hasn't acted
 */
export function getLastPlayerAction(player: Doc<'players'>, game: Doc<'games'>) {
  const actions = game.currentHandActions
    // .filter((a) => a.playerId === player._id && a.boardState === game.boardState && a.action !== 'blind')
    .filter((a) => a.playerId === player._id && a.boardState === game.boardState)
    .sort((a, b) => b.timestamp - a.timestamp)

  return actions[0] // Most recent action with timestamp and type!
}

/**
 * Get game configuration for a game
 * @param ctx - Convex context
 * @param gameId - Game ID
 * @returns Game configuration document or null if not found
 */
export async function getGameConfig(
  ctx: QueryCtx | MutationCtx,
  gameId: Id<'games'>
): Promise<Doc<'gameConfigs'> | null> {
  const config = await ctx.db
    .query('gameConfigs')
    .withIndex('by_game', (q) => q.eq('gameId', gameId))
    .first()

  return config
}

/**
 * Get total pot amount including main pot and all side pots
 *
 * @param game - Game document
 * @returns Total chips across main pot and all side pots
 *
 * @example
 * ```typescript
 * const game = await ctx.db.get(gameId)
 * const totalPot = getTotalPot(game)
 * // Returns: sum of all pot amounts
 * ```
 */
export function getTotalPot(game: Doc<'games'>): number {
  return getTotalPotAmount(normalizePots(game.pots))
}

/**
 * Generate a random 6-character alphanumeric game code.
 * Uses uppercase letters and digits (excluding confusing characters like 0/O, 1/I/L).
 * Used for game codes and schedule hashes.
 *
 * @returns 6-character alphanumeric code
 */
export function generateGameCode(): string {
  return alphanumeric6()
}

/**
 * Generate a unique game code that doesn't already exist in the database
 *
 * @param ctx - Mutation context
 * @param maxAttempts - Maximum number of attempts to find unique code (default: 10)
 * @returns Unique 6-character game code
 * @throws Error if unable to generate unique code after max attempts
 */
export async function generateUniqueGameCode(ctx: MutationCtx, maxAttempts = 10): Promise<string> {
  for (let i = 0; i < maxAttempts; i++) {
    // generateGameCode already returns uppercase (nanoid with uppercase-only alphabet)
    const code = generateGameCode()

    // Check if code already exists
    const existingGame = await ctx.db
      .query('games')
      .withIndex('by_code', (q) => q.eq('code', code))
      .first()

    if (!existingGame) {
      return code
    }
  }

  throw new Error(`Failed to generate unique game code after ${maxAttempts} attempts`)
}

/**
 * Select a random host from human players only
 * CRITICAL: Only considers human players, never bots
 *
 * @param players - Array of player documents
 * @returns Randomly selected human player's userId, or undefined if no human players
 */
export function selectRandomHost(players: Doc<'players'>[]): string | undefined {
  // CRITICAL: Only consider human players, never bots
  const humanPlayers = players.filter((p) => p.kind === 'human')
  if (humanPlayers.length === 0) {
    return undefined
  }
  const randomIndex = Math.floor(Math.random() * humanPlayers.length)
  return humanPlayers[randomIndex].userId
}

// ========== GAME MODE UTILITIES ==========

/** Valid game mode values — single source of truth for game classification */
export type GameMode = 'practice' | 'private' | 'public' | 'community' | 'daily10' | 'mtt'

// Re-exported so server code has a single import site for seat predicates.
// The module itself is dependency-free so the app can import it directly.
export {
  INELIGIBLE_PLAYER_STATES,
  isEligibleToPlay,
  countEligiblePlayers,
  countEligibleHumans,
  countEligibleBots
} from './playerEligibility'

/** Helper: is this a practice game? (controls pause-on-leave, bot behavior) */
export function isPracticeMode(mode: string): boolean {
  return mode === 'practice'
}

/** Helper: is this a solo game? (single human vs bots — practice, daily10) */
export function isSoloMode(mode: string): boolean {
  return mode === 'practice' || mode === 'daily10'
}

/** Helper: is this a multiplayer game? (anything non-solo) */
export function isMultiplayerMode(mode: string): boolean {
  return !isSoloMode(mode)
}

/**
 * Helper: is this a game spawned from a one-off lounge schedule?
 * (oneOff schedule row with a loungeId — e.g. a "Now" lounge table). These games are
 * cleaned up only by the idle/pregame cron or an explicit host end — never auto-ended
 * on player-leave; instead they pause and wait for players to (re)join.
 */
export function isOneOffLoungeGame(game: Doc<'games'>): boolean {
  return game.scheduleTable === 'oneOff' && game.loungeId != null
}

// ========== LOUNGE MEMBERSHIP UTILITIES ==========

/**
 * Fetch the lounge membership record for a user.
 * Returns null when the user has no record at all.
 * Use isActiveMember() on the result to check active status,
 * or access the full doc for role/permission checks.
 */
export async function getLoungeMember(ctx: QueryCtx | MutationCtx, loungeId: Id<'lounges'>, userId: string) {
  return ctx.db
    .query('loungeMembers')
    .withIndex('by_lounge_and_user', (q) => q.eq('loungeId', loungeId).eq('userId', userId))
    .first()
}

/** Returns true when the member doc exists and is active. */
export function isActiveMember(member: { status: string } | null): boolean {
  return member?.status === 'active'
}

/**
 * Upsert the caller's lounge presence row and set whether they're seated in a game.
 *
 * Used by the game flow (not the lobby heartbeat): joining a lounge game sets
 * `isInGame: true`, leaving sets it back to `false`. Keeps `lastSeenAt` fresh so the
 * member stays inside the 4-minute online window while at the table. Created rows are
 * tagged `createdBy: 'joinGame'` so we can reason about where the row originated.
 */
export async function upsertLoungePresenceInGame(
  ctx: MutationCtx,
  loungeId: Id<'lounges'>,
  userId: string,
  userName: string,
  isInGame: boolean
) {
  const existing = await ctx.db
    .query('loungePresence')
    .withIndex('by_lounge_and_user', (q) => q.eq('loungeId', loungeId).eq('userId', userId))
    .first()

  if (existing) {
    await ctx.db.patch(existing._id, { lastSeenAt: Date.now(), isInGame })
  } else {
    await ctx.db.insert('loungePresence', {
      loungeId,
      userId,
      userName,
      lastSeenAt: Date.now(),
      isInGame,
      createdBy: 'joinGame'
    })
  }
}
